import { useEffect, useRef, useState } from "react";
import { TEXTO_AVISO_ROSTRO } from "@huayruro/shared";
import { aceptarRostro, estadoRostro, type EstadoRostro } from "../lib/rostro";

// «Registrar cara» — Clientes por cara v1 (tramo 10, D-C5). El AVISO se muestra tal cual (es la constancia de lo
// que la persona acepta) y es el CLIENTE quien toca «Acepto». Después la webcam del mostrador toma tres fotos, saca
// la huella en la compu y borra las fotos: aquí solo se espera a que el servidor diga «registrada».
type Paso = "cargando" | "ya" | "cerrado" | "aviso" | "fotos" | "listo" | "sin_fotos" | "error";

const CADA_MS = 1_500;
const ESPERA_MAX_MS = 110_000; // el «Acepto» vive 2 minutos en el servidor

export function RegistrarCaraModal({ clienteId, nombre, onCerrar }: { clienteId: string; nombre: string; onCerrar: () => void }) {
  const [paso, setPaso] = useState<Paso>("cargando");
  const [estado, setEstado] = useState<EstadoRostro | null>(null);
  const [error, setError] = useState<string | null>(null);
  const desde = useRef(0);

  useEffect(() => {
    let vivo = true;
    estadoRostro(clienteId)
      .then((e) => {
        if (!vivo) return;
        setEstado(e);
        setPaso(e.estado === "registrada" ? "ya" : e.estado === "esperando_fotos" ? "fotos" : e.registro_abierto === false ? "cerrado" : "aviso");
        desde.current = Date.now();
      })
      .catch((e: unknown) => {
        if (!vivo) return;
        setError(e instanceof Error ? e.message : String(e));
        setPaso("error");
      });
    return () => {
      vivo = false;
    };
  }, [clienteId]);

  // Mientras la webcam toma las fotos, se pregunta al servidor si ya llegaron las huellas.
  useEffect(() => {
    if (paso !== "fotos") return;
    const t = window.setInterval(() => {
      if (Date.now() - desde.current > ESPERA_MAX_MS) {
        setPaso("sin_fotos");
        return;
      }
      void estadoRostro(clienteId)
        .then((e) => {
          setEstado(e);
          if (e.estado === "registrada") setPaso("listo");
          else if (e.estado === "sin_cara") setPaso("sin_fotos");
        })
        .catch(() => undefined);
    }, CADA_MS);
    return () => window.clearInterval(t);
  }, [paso, clienteId]);

  async function acepto() {
    try {
      await aceptarRostro(clienteId);
      desde.current = Date.now();
      setPaso("fotos");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPaso("error");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink-surface/40 p-0 backdrop-blur sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Registrar cara">
      <div className="flex max-h-[92vh] w-full flex-col gap-4 rounded-t-[14px] border border-line bg-card p-5 shadow-[0_10px_30px_rgba(36,29,26,0.25)] sm:max-w-md sm:rounded-[14px] sm:p-6" data-testid="registrar-cara">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-ink">Registrar cara</h2>
          <button type="button" onClick={onCerrar} className="min-h-11 px-2 text-[13px] text-ink-2 underline">
            Cerrar
          </button>
        </div>
        <p className="text-[13px] text-ink-2">
          Cliente: <strong className="text-ink">{nombre}</strong>
        </p>

        {paso === "cargando" && <p className="text-[13px] text-ink-3">Revisando…</p>}

        {paso === "ya" && (
          <p className="rounded-[10px] bg-ok-soft px-3 py-2 text-[13.5px] text-ok">
            Ya tiene su cara registrada{estado?.registrado_en ? ` desde el ${new Date(estado.registrado_en).toLocaleDateString("es-PE", { timeZone: "America/Lima" })}` : ""}. Para cambiarla, el encargado la borra en
            Panel › Clientes › Reconocimiento.
          </p>
        )}

        {paso === "cerrado" && (
          <p className="rounded-[10px] bg-inset px-3 py-3 text-[13.5px] text-ink-2" data-testid="registro-cerrado">
            El registro de caras todavía no está activo: se activa cuando el dueño apruebe el aviso para los clientes.
          </p>
        )}

        {paso === "aviso" && (
          <>
            <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-3">Muéstrale la pantalla al cliente</p>
            <p className="rounded-[10px] border border-line-inset bg-inset px-4 py-3 text-[16px] leading-relaxed text-ink" data-testid="aviso-rostro">
              {TEXTO_AVISO_ROSTRO}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button type="button" onClick={() => void acepto()} className="min-h-[52px] flex-1 rounded-[10px] bg-ok px-4 text-[16px] font-bold text-white hover:opacity-90">
                Acepto
              </button>
              <button type="button" onClick={onCerrar} className="min-h-[52px] flex-1 rounded-[10px] border border-line-input bg-card px-4 text-[15px] font-semibold text-ink-emph hover:bg-hover-btn">
                No, gracias
              </button>
            </div>
            <p className="text-[11.5px] text-ink-3">Lo toca el cliente. Si dice que no, se cierra y no se guarda nada.</p>
          </>
        )}

        {paso === "fotos" && (
          <p className="rounded-[10px] bg-info-soft px-3 py-3 text-[14px] text-info-ink" data-testid="esperando-fotos">
            Pídele que mire a la cámara del mostrador unos segundos… <span className="text-ink-3">(se toman 3 fotos y se borran en la compu)</span>
          </p>
        )}

        {paso === "listo" && (
          <p className="rounded-[10px] bg-ok-soft px-3 py-3 text-[14px] font-semibold text-ok" data-testid="cara-registrada">
            ✓ Listo. La próxima vez la cámara te sugerirá que es {nombre}.
          </p>
        )}

        {paso === "sin_fotos" && (
          <div className="flex flex-col gap-2">
            <p className="rounded-[10px] bg-warn-soft px-3 py-2 text-[13.5px] text-warn">No llegaron las fotos. ¿La webcam está conectada y el cliente frente a ella?</p>
            <button type="button" onClick={() => setPaso("aviso")} className="min-h-11 rounded-[9px] border border-line-input bg-card text-[13.5px] font-semibold text-ink-emph">
              Volver a intentar
            </button>
          </div>
        )}

        {paso === "error" && <p className="rounded-[10px] bg-accent-soft px-3 py-2 text-[13.5px] text-accent-ink">{error}</p>}
      </div>
    </div>
  );
}
